import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import BaseModal from 'src/components/modals/base-modal/BaseModal';
import { TestIDs } from 'src/constants/testIDs.constants';
import { useStore } from 'src/contexts/StoreContext';
import { DynamicModalProps } from 'src/components/modals/dynamic-modals/DynamicModal.types';
import { SAVED_PROJECTS_URL } from 'src/app/settings';
import { allowExit } from 'src/_shared/utils/prevent-exit/preventExit';
import { isWeb } from 'src/_shared/utils/isWeb';
import TextLato from 'src/components/text/text-lato/TextLato';
import { styles } from './ModalProductNotAvailable.styles';
import { DRAWER_POSITIONS, DRAWER_VIEWS } from 'src/store/drawer/drawer.constants';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { parseAnalyticsParamsByProduct } from 'src/_shared/analytics.utils';

enum PRODUCT_NOT_AVAILABLE_ACTIONS {
  CLOSE = 'close',
  CONTINUE_EDITING = 'continue_editing',
  BACK_TO_PROJECTS = 'back_to_projects',
  LOGIN = 'login',
}

export const ModalProductNotAvailable = observer(
  ({ type, dynamicModalVisible, dynamicModalLayer: { id } }: DynamicModalProps) => {
    const {
      i18n,
      user,
      dynamicModal: { closeModal },
      drawer: { openDrawer },
      project: { activeProduct },
    } = useStore();
    const { isMobile } = useResponsiveScreen();

    useEffect(() => {
      amplitude.trackEvent(AnalyticsEvents.personalize.productNotAvailableView, {
        ...parseAnalyticsParamsByProduct(activeProduct),
        is_logged: user.isLogged,
      });
    }, []);

    const trackClick = (action: PRODUCT_NOT_AVAILABLE_ACTIONS): Promise<void> =>
      amplitude.trackEvent(AnalyticsEvents.personalize.productNotAvailableClick, {
        ...parseAnalyticsParamsByProduct(activeProduct),
        is_logged: user.isLogged,
        action,
      });

    const handleClose = (): void => {
      trackClick(PRODUCT_NOT_AVAILABLE_ACTIONS.CLOSE);
      closeModal();
    };

    const handleContinueEditing = (): void => {
      trackClick(PRODUCT_NOT_AVAILABLE_ACTIONS.CONTINUE_EDITING);
      closeModal();
    };

    const goToSavedProjects = async (): Promise<void> => {
      await trackClick(PRODUCT_NOT_AVAILABLE_ACTIONS.BACK_TO_PROJECTS);
      if (isWeb()) {
        allowExit();
        window.location.href = SAVED_PROJECTS_URL;
      }
    };

    const openLogin = (): void => {
      trackClick(PRODUCT_NOT_AVAILABLE_ACTIONS.LOGIN);
      closeModal();
      openDrawer({
        drawerView: DRAWER_VIEWS.Login,
        drawerPosition: DRAWER_POSITIONS.Right,
      });
    };

    return (
      <BaseModal
        testID={TestIDs.PRODUCT_NOT_AVAILABLE}
        modalIcon={null}
        header={
          <TextLato style={[styles.title, isMobile && styles.titleMobile]}>
            {i18n.t('modal-product-not-available:title')}
          </TextLato>
        }
        body={
          <TextLato style={[styles.description, isMobile && styles.descriptionMobile]}>
            {i18n.t(`modal-product-not-available:${user.isLogged ? 'description-saved' : 'description-guest'}`)}
          </TextLato>
        }
        discardText={i18n.t('modal-product-not-available:continue-editing')}
        handleDiscard={handleContinueEditing}
        confirmText={i18n.t(`modal-product-not-available:${user.isLogged ? 'back-to-projects' : 'login'}`)}
        handleConfirm={user.isLogged ? goToSavedProjects : openLogin}
        onClose={handleClose}
        onCloseOutside={handleClose}
        isVisible={dynamicModalVisible}
        type={type}
        id={id}
        modalWidth={500}
        modalStyle={styles.modal}
      />
    );
  },
);
